import React, { useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { useAssessmentWalkthrough } from "../../providers/AssessmentWalkthroughProvider";
import { Box, Checkbox, FormControlLabel, useMediaQuery } from "@mui/material";
import { getAssessmentWalkthroughStyle } from "../../components/customstyles/AssessmentWalkthroughStyle";
import { useTheme } from "@emotion/react";
import ReactPlayer from "react-player";
import { usePatientAndAssessmentDataHelper } from "../../providers/PatientAndAssessmentDataHelperProvider";
import { recordCustomEvent } from "../../pinpointConfig";

const useStyles = () => {
  const theme = useTheme();
  return getAssessmentWalkthroughStyle(theme);
};

const WalkthroughSteps = [
  {
    ask: true,
    title: "Is there anything in your mouth?",
    description:
      "If yes, ask the patient to remove it. Gum chewing may resemble orofacial movements which are among the most commonly observed in TD.",
  },
  {
    ask: true,
    title: "Do you wear dentures? Do your teeth or dentures bother you now?",
    description:
      "Problems with teeth or dentures can lead to mistaken diagnosis of dyskinesia.",
  },
  {
    ask: true,
    title:
      "Do you notice any abnormal movements in your mouth, face, hands, or feet?",
    description:
      "If yes, ask the patient to describe the movements and how bothersome they are. Patients may be unaware of their movements or impact. Consider seeking caregiver feedback about abnormal movements.",
  },
  {
    title: "Observe the entire body and rate any abnormal movements.",
    description:
      "Have the patient sit in a firm chair, that has a straight back and no arms, with hands on knees, legs slightly apart, and feet flat on the floor.",
    url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_4_AIMS_Tutorial.mp4",
  },
  {
    title: "Observe hands and other body areas.",
    description:
      "Ask the patient to sit with hands hanging and unsupported between the legs or over the knees.",
    url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_5_AIMS_Tutorial.mp4",
  },
  {
    title: "Observe the tongue at rest.",
    description: "Ask the patient to open his or her mouth. Do this twice.",
    url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_6_AIMS_Tutorial.mp4",
  },
  {
    title: "Observe abnormalities of tongue movements.",
    description:
      "Ask the patient to protrude his or her tongue. Do this twice.",
    url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_7_AIMS_Tutorial.mp4",
  },
  {
    title: "Observe the face and legs.",
    description:
      "Ask the patient to tap his or her thumb with each finger as rapidly as possible for 10 to 15 seconds, first with the right hand, then with the left hand.",
    url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_8_AIMS_Tutorial.mp4",
  },
  {
    title: "Observe the left and right arms.",
    description:
      "Flex and extend the patient’s left and right arms, one at a time. Note any rigidity or muscle stiffness that could indicate the potential presence of parkinsonism.",
    url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_9_AIMS_Tutorial.mp4",
  },
  {
    title: "Observe for truncal movements.",
    description:
      "Ask the patient to stand, arms to the side, so they can be observed in profile and straight on.",
    url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_10_AIMS_Tutorial.mp4",
  },
  {
    title: "Observe the face and legs.",
    description:
      "Ask the patient to extend both arms out in front, palms down.",
    url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_11_AIMS_Tutorial.mp4",
  },
  {
    title: "Observe hands and gait.",
    description:
      "Have the patient walk a few paces, turn, and walk back to the chair. Do this twice.",
    url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_12_AIMS_Tutorial.mp4",
  },
];

export default function AssessmentWalkthrough({ showCheckBox }) {
  const styles = useStyles();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const { saveDoNotShowAssessmentWalkthrough } =
    usePatientAndAssessmentDataHelper();
  const { showWalkthroughDialog, showOrHideAssessmentWalkthrough } =
    useAssessmentWalkthrough();
  const handleClose = () => {
    showOrHideAssessmentWalkthrough(false);
  };
  const handleCheckBox = async (event) => {
    let checked = event.target.checked;
    await saveDoNotShowAssessmentWalkthrough(checked);
  };
  useEffect(() => {
    recordCustomEvent(window.location.pathname + "/tutorial");
  }, []);
  return (
    <React.Fragment>
      <Dialog
        className="FSUnmask"
        fullScreen={isMobile}
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={showWalkthroughDialog}
        sx={{ zIndex: 10000 }}
      >
        <Typography
          sx={{ m: 0, p: 2, color: styles.headingTextColor }}
          variant="p"
          fontSize={20}
          fontWeight={500}
        >
          Assessment Walkthrough
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          id="main-content"
          sx={{
            padding: "16px 40px !important",
            height: isMobile ? "100vh" : "490px",
          }}
          dividers
        >
          <Box>
            <Typography
              variant="p"
              fontSize={20}
              fontWeight={500}
              color={styles.headingTextColor}
            >
              Before you begin the AIMS assessment
            </Typography>
            <Box sx={{ marginY: "20px", color: styles.textColor }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="p" fontSize={14}>
                  1.
                </Typography>
                <Typography variant="p" fontSize={14}>
                  Either before or after completing the examination procedure,
                  observe the patient unobtrusively at rest (e.g., in the
                  waiting room).
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="p" fontSize={14}>
                  2.
                </Typography>
                <Typography variant="p" fontSize={14}>
                  The chair to be used in this examination should be a hard,
                  firm one without arms.
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography
              variant="p"
              fontSize={20}
              fontWeight={500}
              color={styles.headingTextColor}
            >
              Steps
            </Typography>
            <br />
            <br />
            <Typography variant="p" fontSize={14}>
              For steps 1-3, ask the patient a few questions and confirm they
              have nothing in their mouth.
            </Typography>

            {WalkthroughSteps.map((step, index) => {
              return (
                <Box sx={{ marginY: "10px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mb: "10px",
                    }}
                  >
                    <Typography variant="p" fontSize={14}>
                      {index + 1}.
                    </Typography>
                    <Box>
                      {step.ask && (
                        <Typography variant="p" fontSize={14} marginRight={1}>
                          {"Ask the patient:"}
                        </Typography>
                      )}

                      <Typography
                        variant="p"
                        fontSize={14}
                        fontWeight={"bold"}
                        marginRight={1}
                      >
                        {step.title}
                      </Typography>
                      <Typography variant="p" fontSize={14}>
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                  {step.url && (
                    <Box>
                      <ReactPlayer
                        width="100%"
                        url={step.url}
                        controls={true}
                        height="100%"
                      />
                      <Typography variant="p" fontSize={14}>
                        {"Patient images used with permission."}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            ...styles.bottomDiv,
            justifyContent: showCheckBox ? "space-between" : "flex-end",
          }}
        >
          {showCheckBox && (
            <FormControlLabel
              control={
                <Checkbox sx={styles.checkbox} onChange={handleCheckBox} />
              }
              label={
                <Typography variant="p" fontSize={14}>
                  Don’t show me this again
                </Typography>
              }
            />
          )}

          <Button variant="text" onClick={handleClose}>
            <Typography variant="p" fontWeight={"bold"} sx={styles.getStarted}>
              Get Started
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
